import {
  View,
  FlatList,
  Platform,
  StyleSheet,
  Button,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { GTFonts, theme } from '../../theme';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useAuth from '../../hooks/useAuth';
import { ExtrasContext } from '../../Context/ExtrasContext';
import {
  addFilteredPartialOrder,
  addPartialOrder,
  clearPartialOrders,
  clearFilteredPartialOrder,
  seedFilteredPartialFromLocal,
  OrderDoc,
} from '../../redux/OrdersSlice';
import { useRef } from 'react';
import AnimatedLottieView from 'lottie-react-native';
import ErrorContext from '../../Context/ErrorContext';
import { useAppSelector } from '../../redux/helper';
import {
  getFilteredOrderswithStatus,
  getPartialOrders,
} from '../../api/orders';
import Typography from '../../components/Reusables/Typography';
import SearchContext from '../../Context/SearchContext';
import { useNavigationState } from '@react-navigation/native';
import {
  formatWithCommas,
  replaceCurrencyString,
  fs,
  s,
  v,
} from '../../utils/libs';
import { useNavigation as useNavigationContext } from '../../Context/NavigationContext';
import { shouldUseWidthConstraint } from '../../utils/layoutUtils';
import AppSkeleton from '../../components/Reusables/AppSkeleton';
import { formatDisplayDateTime } from '../../utils/formatDisplayDateTime';

type PartialOrdersProps = {
  navigation: any;
  dateFilterActive?: boolean;
  dateFilterStartISO?: string;
  dateFilterEndISO?: string;
};

const PartialOrders = ({
  navigation,
  dateFilterActive = false,
  dateFilterStartISO,
  dateFilterEndISO,
}: PartialOrdersProps) => {
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWidthConstraint(width, height);
  const partial = useAppSelector(state => state?.orders.partial);

  const filteredPartial = useAppSelector(
    state => state?.orders.filteredPartial,
  );
  const [refresh, setRefresh] = useState(false);
  const axiosprivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const { refreshing } = useContext(ExtrasContext);
  const { handleError } = useContext(ErrorContext);
  const { orderSearchId } = useContext(SearchContext);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );
  const activeTabName = useNavigationState(
    state => state.routes[state.index].name,
  );
  const { setOrderDetailsData } = useNavigationContext();
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => replaceCurrencyString('ksh', currency),
    [currency],
  );

  const getPartialAllOrders = useCallback(
    async (currentpage: number) => {
      if (!loading) {
        setLoading(true);
        try {
          const response = await getPartialOrders(
            axiosprivate,
            currentpage,
            auth,
            dateFilterActive ? dateFilterStartISO : undefined,
            dateFilterActive ? dateFilterEndISO : undefined,
          );
          if (response.data.status === 200) {
            console.log('getPartialAllOrders', response.data.data);
            dispatch(addPartialOrder(response.data.data));
            setLoading(false);
          } else {
            setLoading(false);
            handleError(response.data.message, 'error', true);
          }
        } catch (error) {
          console.log('getPartialOrdersError', error);
          setLoading(false);
          // handleError(error.message, "error", true);
        }
      }
    },
    [
      loading,
      axiosprivate,
      auth,
      dateFilterActive,
      dateFilterStartISO,
      dateFilterEndISO,
      dispatch,
      handleError,
    ],
  );

  const handleGetFilteredOrdersWithStatus = useCallback(
    async (currentpage: number, searchText?: string, status?: string) => {
      if (!loading) {
        setLoading(true);
        try {
          const response = await getFilteredOrderswithStatus(
            axiosprivate,
            currentpage,
            auth,
            searchText ?? '',
            status ?? '',
          );
          if (response.data.status === 200) {
            console.log('getFilteredOrderswithStatus', response.data.data);
            dispatch(addFilteredPartialOrder(response.data.data));
            setLoading(false);
          } else {
            setLoading(false);
            handleError(response.data.message, 'error', true);
          }
        } catch (error) {
          console.log('getFilteredOrderswithStatusError', error);
          setLoading(false);
          // handleError(error.message, "error", true);
        }
      }
    },
    [loading, axiosprivate, auth, dispatch, handleError],
  );

  useEffect(() => {
    if (!dateFilterActive) return;

    dispatch(clearPartialOrders());
    requestAnimationFrame(() => {
      getPartialAllOrders(1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterActive, dateFilterStartISO, dateFilterEndISO]);

  useEffect(() => {
    // Only act when this tab is active
    if (activeTabName.toLowerCase() !== 'partially paid') return;

    if (searchTimeout) clearTimeout(searchTimeout);
    const term = (orderSearchId || '').trim();
    if (term.length > 1) {
      // Local-first seed
      dispatch(seedFilteredPartialFromLocal(term));

      // Background backend merge
      const timeoutId = setTimeout(() => {
        handleGetFilteredOrdersWithStatus(1, term, 'PARTIAL');
      }, 300);
      setSearchTimeout(timeoutId);
    } else {
      dispatch(clearFilteredPartialOrder());
    }

    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [orderSearchId, activeTabName]);

  const LoadMoreOrders = useCallback(() => {
    if (partial.hasNextPage && partial.docs.length < partial.totalDocs) {
      getPartialAllOrders(partial.nextPage);
    }
  }, [
    partial.hasNextPage,
    partial.docs.length,
    partial.totalDocs,
    partial.nextPage,
    getPartialAllOrders,
  ]);

  const loadMoreFilteredOrders = useCallback(() => {
    const status = 'PARTIAL';
    if (
      filteredPartial.hasNextPage &&
      filteredPartial.docs.length < filteredPartial.totalDocs
    ) {
      handleGetFilteredOrdersWithStatus(
        filteredPartial.nextPage,
        orderSearchId,
        status,
      );
    }
  }, [
    filteredPartial.hasNextPage,
    filteredPartial.docs.length,
    filteredPartial.totalDocs,
    filteredPartial.nextPage,
    orderSearchId,
    handleGetFilteredOrdersWithStatus,
  ]);
  const Refresh = useCallback(() => {
    setRefresh(true);
    getPartialAllOrders(1);
    setRefresh(false);
  }, [getPartialAllOrders]);

  const ref = useRef(true);

  useEffect(() => {
    const firstRender = ref.current;
    if (firstRender) {
      ref.current = false;
    } else {
      Refresh();
    }
  }, [refreshing]);

  const handleOrderPress = useCallback(
    (order: OrderDoc) => {
      navigation.navigate('OrderDetails');

      setTimeout(() => {
        setOrderDetailsData({
          order: order.bp_sales_products_information,
          orderID: order.bp_order_id,
          AssignedStaffInformation:
            order.bp_order_assigned_staff_information ?? null,
          ReceiptID: order.bp_order_master_receipt_reference_id ?? '',
          Payment_Information: order.bp_order_master_payment_information,
          CustomerName: order.bp_order_customer_information,
          ordertype: order.bp_order_master_order_status,
          updatedAt: order.bp_order_master_updated_at,
        });
      });
    },
    [navigation, setOrderDetailsData],
  );

  const render = useCallback(
    ({ item: order, index }: { item: OrderDoc; index: number }) => {
      if (!order) {
        return null;
      }
      const rowNumber = index + 1;
      return (
        <View style={styles.container} key={order?.bp_order_id}>
          <Pressable
            unstable_pressDelay={80}
            accessibilityActions={[
              {
                name: 'View Order',
                label: 'View Order',
              },
            ]}
            accessibilityLabel={`View Order ${rowNumber}`}
            accessibilityHint="Tap to view order details"
            style={({ pressed, hovered }: any) => [
              styles.section,
              Platform.OS === 'web' && styles.sectionWeb,
              Platform.OS === 'web' && hovered && styles.sectionHovered,
              pressed && { backgroundColor: theme.colors.textTertiary, opacity: 0.2 },
            ]}
            onPress={() => handleOrderPress(order)}>
            <View style={styles.rowWithNumber}>
              <View style={styles.rowNumberColumn}>
                <Typography style={styles.rowNumberText}>{rowNumber}</Typography>
              </View>
              <View style={styles.rowContent}>
            <View style={styles.top} key={order.bp_order_id}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Typography style={styles.name}>
                  {order?.bp_order_customer_information?.length > 16
                    ? `${order.bp_order_customer_information.substring(0, 16)}...`
                    : order.bp_order_customer_information}
                </Typography>
                <Typography style={[styles.productCount, { marginLeft: 3 }]}>
                  {'•'}
                  {order.bp_sales_products_information?.length}
                  {'\u00A0'}
                  {order.bp_sales_products_information?.length > 1
                    ? 'Items'
                    : 'Item'}
                </Typography>
              </View>
              <Typography style={[styles.text]}>
                Balance:{'\u00A0'}
                <Typography
                  style={[styles.priceText, { fontFamily: GTFonts.semibold }]}>
                  {currencyText}
                  {'\u00A0'}
                  {formatWithCommas(
                    order?.bp_order_master_payment_information
                      ?.bp_payment_amount_due,
                  )}
                </Typography>
              </Typography>
            </View>
            <View style={styles.bottom}>
              <Typography style={[styles.text, { fontSize: fs(14) }]}>
                {formatDisplayDateTime(order.bp_order_master_updated_at, '', {
                  relativeDayLabels: true,
                })}
              </Typography>
              <Typography style={styles.text}>
                {order.bp_order_master_receipt_reference_id ? (
                  <>
                    Receipt Id:{'\u00A0'}
                    <Typography
                      style={[styles.text, { fontFamily: GTFonts.semibold }]}>
                      {order.bp_order_master_receipt_reference_id}
                    </Typography>
                  </>
                ) : null}
              </Typography>
            </View>
              </View>
            </View>
          </Pressable>
        </View>
      );
    },
    [handleOrderPress],
  );
  const keyExtractor = useCallback((item: OrderDoc) => item?.bp_order_id, []);

  // Memoized filtering logic to replace the useLayoutEffect
  const filteredPartialOrders = useMemo(() => {
    const isAlphanumeric = /^[a-z0-9]+$/i.test(orderSearchId);
    const sourceDocs =
      orderSearchId.length > 0 ? filteredPartial?.docs : partial?.docs;

    if (!sourceDocs) return [];

    return sourceDocs.filter(order => {
      if (isAlphanumeric) {
        return (
          order.bp_order_master_receipt_reference_id
            .toLowerCase()
            .includes(orderSearchId.toLowerCase()) ||
          order.bp_order_customer_information
            .toLowerCase()
            .includes(orderSearchId.toLowerCase())
        );
      }
      return true; // If not alphanumeric, include all orders
    });
  }, [orderSearchId, filteredPartial?.docs, partial?.docs]);

  return (
    <View
      style={[
        styles.listContainer,
        constrainWidth ? styles.listContainerConstrained : null,
      ]}>
      <FlatList
        data={filteredPartialOrders}
        keyExtractor={keyExtractor}
        initialNumToRender={10}
        refreshing={refresh}
        onEndReachedThreshold={0.8}
        // maxToRenderPerBatch={10}
        onEndReached={
          orderSearchId.length > 1 ? loadMoreFilteredOrders : LoadMoreOrders
        }
        onRefresh={Refresh}
        renderItem={render}
        ListFooterComponent={
          loading ? (
            <View style={{ paddingVertical: v(10), paddingHorizontal: s(4) }}>
              <AppSkeleton mode="orders_list" count={2} />
            </View>
          ) : null
        }
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

export default PartialOrders;

const styles = StyleSheet.create({
  container: {},
  listContainer: {
    flex: 1,
    backgroundColor: theme.colors.surface,
  },
  listContainerConstrained: {
    maxWidth: WEB_MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: s(24),
  },
  activityindicator: {
    color: theme.colors.textGray900,
    flex: 1,
    marginTop: v(20),
    justifyContent: 'center',
  },
  productCount: {
    fontFamily: GTFonts.regular,
    color: theme.colors.shadow,
    fontSize: fs(14),
    lineHeight: fs(20),
  },
  rowWithNumber: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowNumberColumn: {
    minWidth: s(24),
    marginRight: s(8),
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowNumberText: {
    fontSize: Platform.OS === 'web' ? fs(15) : fs(13),
    lineHeight: Platform.OS === 'web' ? fs(18) : fs(16),
    fontFamily: GTFonts.semibold,
    color: theme.colors.text,
    textAlign: 'center',
  },
  rowContent: {
    flex: 1,
  },
  section: {
    borderColor: theme.colors.shadowLight,
    borderBottomWidth: 1,
    padding: s(5),
    paddingTop: v(5),
  },
  sectionWeb: {
    ...(Platform.OS === 'web'
      ? ({
          cursor: 'pointer',
          transition: 'background-color 0.15s ease-out',
          borderRadius: theme.borderRadius?.sm ?? 4,
          paddingHorizontal: s(8),
          marginHorizontal: s(-8),
        } as any)
      : {}),
  },
  sectionHovered: {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  top: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: s(2),
  },
  bottom: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: s(2),
    marginTop: 10,
  },
  name: {
    marginVertical: v(1),
    fontFamily: GTFonts.semibold,
    fontSize: fs(16),
    lineHeight: fs(20),
    color: theme.colors.textGray900,
  },
  text: {
    lineHeight: 20,
    fontFamily: GTFonts.regular,
    fontSize: fs(14),
    color: theme.colors.graphiteDark,
  },
  priceText: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(14),
    color: theme.colors.text,
  },
  safeArea: {
    flex: 1,
  },
});
